import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin } from 'lucide-react';

const teamMembers = [
    { name: "Team Member One", title: "Managing Partner", img: "https://placehold.co/400/400/E5E7EB/1F2937?text=Team+Member", linkedin: "https://linkedin.com" },
    { name: "Team Member Two", title: "General Partner", img: "https://placehold.co/400/400/E5E7EB/1F2937?text=Team+Member", linkedin: "https://linkedin.com" },
    { name: "Team Member Three", title: "Operating Partner", img: "https://placehold.co/400/400/E5E7EB/1F2937?text=Team+Member", linkedin: "https://linkedin.com" },
    { name: "Team Member Four", title: "Venture Partner", img: "https://placehold.co/400/400/E5E7EB/1F2937?text=Team+Member", linkedin: "https://linkedin.com" },
];

const offices = [
    { name: "Estonia (Europe HQ)", description: "Tallinn. EU base for company formation, hiring, and enterprise outreach across the bloc." },
    { name: "Dubai (GCC)", description: "Dubai. Free zone setup, founder visas, and partnerships across the Gulf for regulated and enterprise markets." },
    { name: "Mumbai (India)", description: "Mumbai. Engineering talent, procurement navigation, and scaled go-to-market into India’s enterprise ecosystem." },
];
const principles = [
    "Founder-first, long-term alignment.",
    "Write clearly, decide quickly, measure what matters.",
    "Teach the playbook, then tailor to context."
];

const Team = () => {
    const introText = "Operators, product builders, and market specialists across Tallinn, Dubai, and Mumbai, aligned around founder speed and responsible scale.";

    return (
        <motion.div
            className="bg-background py-12 md:py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto px-6">

                <div className="max-w-6xl mx-auto mb-16 md:mb-24">
                    <motion.h1
                        className="text-4xl md:text-5xl font-bold text-foreground mb-6 font-serif"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                    >
                        Team
                    </motion.h1>

                    {/* Intro Text Wrapper - Kept Left Aligned */}
                    <div className="text-lg md:text-xl text-muted-foreground max-w-3xl">
                        {/* Replaced TextRevealOnScroll with simple motion.p */}
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.4 }}
                        >
                            {introText}
                        </motion.p>
                    </div>
                </div>


                {/* Team Members Grid */}
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 font-serif">Meet the Team</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {teamMembers.map((member, index) => (
                            <motion.div
                                key={member.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="group relative"
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                            >
                                <div className="relative aspect-square w-full overflow-hidden rounded-lg mb-4 shadow-md">
                                    <img
                                        src={member.img}
                                        alt={member.name}
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                    <a
                                        href={member.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="absolute top-3 right-3 p-2.5 bg-card/70 backdrop-blur-sm rounded-full text-foreground opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-primary hover:text-primary-foreground"
                                        aria-label={`${member.name}'s LinkedIn Profile`}
                                    >
                                        <Linkedin size={18} />
                                    </a>
                                </div>
                                <div className="text-left px-1 pb-4">
                                    <h3 className="text-xl font-bold text-foreground group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-cyan-500 group-hover:bg-clip-text group-hover:text-transparent transition-colors duration-300 mb-1">
                                        {member.name}
                                    </h3>
                                    <p className="text-base text-muted-foreground">{member.title}</p>
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 w-0 group-hover:w-full transition-all duration-500 rounded-b-lg"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Principles & Culture Sections */}
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 mt-24 border-t border-border pt-16">
                    <div>
                        <h3 className="text-3xl font-bold text-foreground mb-6 font-serif">Operating Principles</h3>
                        <ul className="space-y-4">
                            {principles.map((p, i) => (
                                <li key={i} className="text-lg text-muted-foreground leading-relaxed">{p}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-3xl font-bold text-foreground mb-6 font-serif">Culture</h3>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            Curiosity over pedigree, independence with accountability, and high signal feedback loops from customers and markets.
                        </p>
                    </div>
                </div>

                {/* Offices Section */}
                <div className="max-w-6xl mx-auto mt-24">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 font-serif">Our Offices</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {offices.map((office, index) => (
                            <motion.div
                                key={office.name}
                                className="bg-card p-8 rounded-lg shadow-sm border border-border group relative overflow-hidden transition-all duration-300 hover:shadow-md"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                                viewport={{ once: true, amount: 0.2 }}
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                            >
                                <h3 className="text-2xl font-bold text-foreground group-hover:bg-gradient-to-r group-hover:from-blue-500 group-hover:to-cyan-500 group-hover:bg-clip-text group-hover:text-transparent transition-colors duration-300 mb-4">
                                    {office.name}
                                </h3>
                                <p className="text-base text-muted-foreground leading-relaxed">{office.description}</p>
                                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 w-0 group-hover:w-full transition-all duration-500 "></div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default Team;